import React, { useMemo } from "react";
import { connect } from "react-redux";
import * as Actions from "../../../redux/actions";
import { AppState } from "../../../redux";
import {
  CompanyIndustries,
  CompanyInvestmentStages,
  CompanySpotlights,
  CompanyTypes,
  companyTypeLabel,
} from "../../../constants/company-info";
import { SetPitchPlanetFilterPayload, SetPitchPlanetFilterAction } from "../../../redux/account/types";
import { Checkbox } from "../../page-framework/checkbox";
import { SVG } from "../../../component-configuration/svgs";
import { SearchFacet } from "@radivision/graphql/lib/ts/graphql/search-hit";

interface ComponentProps {
  facets?: SearchFacet[];
}

interface MapDispatchProps {
  setPitchPlanetFilter: (payload: SetPitchPlanetFilterPayload) => SetPitchPlanetFilterAction;
}
interface MapStateProps {
  filters: Record<string, string[]>;
}

export type Props = ComponentProps & MapDispatchProps & MapStateProps;

interface MenuItem {
  value: string;
  label: string;
  count?: number;
}

interface FilterGroup {
  // ... the CloudSearch field of the pitch document
  key: string;
  title: string;
  // ... shown until the facets load, or when the search returns none for the field
  initial: MenuItem[];
  // ... keep the initial values listed even when no pitch has them yet
  keepInitial?: boolean;
  label?: (value: string) => string;
}

const toItems = (values: string[]): MenuItem[] => values.map((value) => ({ value, label: value }));

// ... structure values are indexed title-cased ("C Corporation"); show the form's label ("LLC")
const structureLabel = (value: string) => companyTypeLabel(value.trim().toUpperCase().replace(/[\s-]+/g, "_")) || value;

export const PITCH_PLANET_FILTER_GROUPS: FilterGroup[] = [
  { key: "industry", title: "Industry", initial: toItems(CompanyIndustries.map((item) => item.title)) },
  { key: "country", title: "Geography", initial: [] },
  { key: "stage", title: "Stage", initial: toItems(CompanyInvestmentStages.map((item) => item.title)) },
  {
    key: "activity",
    title: "Structure",
    initial: CompanyTypes.map((item) => ({ value: item.title, label: item.title })),
    label: structureLabel,
  },
  { key: "spotlight", title: "Spotlight", initial: toItems(CompanySpotlights), keepInitial: true },
];

export const PITCH_PLANET_FILTER_KEYS: string[] = PITCH_PLANET_FILTER_GROUPS.map((group) => group.key);

export const generateMenus = (facets: SearchFacet[] | undefined): Record<string, MenuItem[]> => {
  const menus: Record<string, MenuItem[]> = {};

  PITCH_PLANET_FILTER_GROUPS.forEach((group) => {
    const facet = facets?.find((item) => item?.name === group.key);

    if (!facet) {
      menus[group.key] = group.initial;
      return;
    }

    const found: MenuItem[] = (facet.buckets || [])
      .filter((bucket) => bucket?.value)
      .map((bucket) => ({
        value: bucket.value,
        label: group.label ? group.label(bucket.value) : bucket.value,
        count: bucket.count,
      }));

    if (group.keepInitial) {
      const counted = group.initial.map((item) => found.find((bucket) => bucket.value === item.value) || item);
      const others = found.filter((bucket) => !group.initial.some((item) => item.value === bucket.value));
      menus[group.key] = [...counted, ...others];
    } else {
      menus[group.key] = found;
    }
  });

  return menus;
};

export const Component = ({ filters, setPitchPlanetFilter, facets }: Props) => {
  const menus = useMemo(() => generateMenus(facets), [facets]);

  const handleCheckboxChange = (key: string, value: string) => () => {
    setPitchPlanetFilter({ key, value });
  };

  return (
    <div className="pitch-planet-filters">
      {PITCH_PLANET_FILTER_GROUPS.map(({ key, title }) => {
        const filter = filters[key] || [];
        const menu = menus[key] || [];

        return (
          <div className="filter-tab" key={`filter-tab-${key}`}>
            <a className="filter-name">
              <span>{title}</span>
              {filter.length > 0 ? <span className="filter-count">{filter.length}</span> : null} {SVG.caretRight}
            </a>
            <ul className="sm-scroller">
              {menu.length === 0 ? <li className="filter-empty">None yet</li> : null}
              {menu.map((item: MenuItem) => (
                <li key={`filter-${key}-${item.value}`}>
                  <Checkbox
                    checked={filter.includes(item.value)}
                    onChange={handleCheckboxChange(key, item.value)}
                    label={
                      <div>
                        <span>{item.label}</span>
                        {item.count ? <span>{item.count > 99 ? "99+" : item.count}</span> : null}
                      </div>
                    }
                  />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

const mapStateToProps = (state: AppState): MapStateProps => ({
  filters: state.account.pitchPlanetFilter,
});

const mapDispatchToProps: MapDispatchProps = {
  setPitchPlanetFilter: Actions.setPitchPlanetFilter,
};

export const PitchPlanetFilters = connect(mapStateToProps, mapDispatchToProps)(Component);
